import React, { useContext, useEffect, useRef } from 'react'
import ModalContext, { ModalInjectedProps } from '../ModalContext'

interface ModalPropsPart<T extends ModalInjectedProps> {
  modalProps: OmitSafe<T, keyof ModalInjectedProps>
}

type WithOptionalProps<T extends ModalInjectedProps> = {} extends OmitSafe<
  T,
  keyof ModalInjectedProps
>
  ? Partial<ModalPropsPart<T>>
  : ModalPropsPart<T>

interface OpenModalViaPropBaseProps<T extends ModalInjectedProps> {
  modal: React.ComponentType<T>
  isOpen?: boolean
}

type OpenModalViaPropProps<
  T extends ModalInjectedProps
> = OpenModalViaPropBaseProps<T> & WithOptionalProps<T>

function OpenModalViaProp<T extends ModalInjectedProps>(
  props: OpenModalViaPropProps<T>
): null {
  const { modal, modalProps, isOpen }: typeof props = props
  const { showModal, hideModal } = useContext(ModalContext)

  /*
  Prevent the update of context or props to trigger
  a closing or reopening of the modal
  */
  const showModalRef = useRef(() => {
    showModal(modal, modalProps)
  })

  const hideModalRef = useRef(() => {
    hideModal()
  })

  /* Make sure the refs are always updated */
  useEffect(() => {
    showModalRef.current = () => {
      showModal(modal, modalProps)
    }
  }, [modal, modalProps, showModal])

  useEffect(() => {
    hideModalRef.current = hideModal
  }, [hideModal])

  useEffect(() => {
    return () => {
      isOpen && hideModalRef.current()
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      showModalRef.current()
    } else {
      hideModalRef.current()
    }
  }, [isOpen])

  return null
}

export default OpenModalViaProp
